import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, Download, Trash2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

export const OrganizerRegistrationsTab = ({ API_URL, getAuthHeader }) => {
  const [data, setData] = useState({ total: 0, session_counts: {}, sessions_meta: {}, registrations: [] });

  const fetchRegs = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/organizer-training/registrations`, { headers: getAuthHeader() });
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchRegs(); }, [fetchRegs]);

  const exportCsv = async () => {
    try {
      const r = await fetch(`${API_URL}/api/organizer-training/registrations.csv`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Export failed');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'organizer_training_registrations.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name}'s registration?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/organizer-training/registrations/${r.id}`, { method: 'DELETE', headers: getAuthHeader() });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Registration removed');
      fetchRegs();
    } catch (e) { toast.error(e.message); }
  };

  const meta = data.sessions_meta || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="organizer-registrations-panel">
      <div className="px-6 py-5 bg-gradient-to-r from-rose-900 to-slate-900 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-rose-300 mb-1 flex items-center gap-2">
            <Megaphone className="w-3.5 h-3.5" aria-hidden="true" /> Organizer Training Series
          </p>
          <h3 className="text-lg font-bold">Registrations — November 10, 12 &amp; 19</h3>
          <p className="text-white/70 text-xs mt-0.5">
            {data.total} registrant{data.total !== 1 ? 's' : ''} ·
            {Object.keys(meta).map(k => ` ${meta[k].label}: ${data.session_counts[k] || 0}`).join(' ·')}
          </p>
        </div>
        <button onClick={exportCsv} data-testid="organizer-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/30 hover:bg-white/10 rounded-lg text-sm font-semibold">
          <Download className="w-4 h-4" aria-hidden="true" /> Export CSV
        </button>
      </div>
      {data.registrations.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm" data-testid="organizer-regs-empty">
          <CalendarDays className="w-10 h-10 mx-auto mb-3 text-slate-300" aria-hidden="true" />
          No registrations yet — signups from the Professional Development page will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left py-2 px-4">Name</th>
                <th className="text-left py-2 px-4">Organization / Role</th>
                <th className="text-center py-2 px-4">Attendance</th>
                <th className="text-left py-2 px-4">Sessions</th>
                <th className="text-left py-2 px-4">Heard Via</th>
                <th className="text-center py-2 px-4">Registered</th>
                <th className="text-center py-2 px-4">Reminders</th>
                <th className="text-center py-2 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.registrations.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`organizer-reg-row-${r.id}`}>
                  <td className="py-2.5 px-4">
                    <p className="font-medium text-slate-800">{r.name}</p>
                    <p className="text-xs text-slate-400">{r.email}</p>
                  </td>
                  <td className="py-2.5 px-4 text-xs text-slate-600">{[r.organization, r.role].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="py-2.5 px-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${r.attendance === 'virtual' ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`organizer-attendance-${r.id}`}>
                      {r.attendance === 'virtual' ? 'Virtual' : 'In person'}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex flex-wrap gap-1">
                      {(r.sessions || []).map(k => (
                        <span key={k} className="inline-block px-2 py-0.5 bg-rose-100 text-rose-800 rounded-full text-[10px] font-bold">{meta[k]?.label || k}</span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">{r.heard_about || '—'}</td>
                  <td className="py-2.5 px-4 text-center text-xs text-slate-500">{(r.created_at || '').slice(0, 10)}</td>
                  <td className="py-2.5 px-4 text-center text-xs text-slate-500" title={(r.reminder_log || []).map(l => `${meta[l.session]?.label || l.session}: ${l.status} ${(l.at || '').slice(0, 10)}`).join('\n')}>
                    {(r.reminders_sent || []).length ? (r.reminders_sent || []).map(k => meta[k]?.label).join(', ') : '—'}
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    <button onClick={() => remove(r)} aria-label={`Remove ${r.name}`} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`organizer-reg-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
